import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer';
import format from 'date-fns/format';
import * as pdfjs from 'pdfjs-dist/webpack';
import { FC, useMemo } from 'react';
import font from 'src/assets/fonts/Roboto-Bold.ttf';
import { InvoiceDetailsResponse } from 'src/services/useInvoiceDetailsApi';
import { GLBreakoutLineItems, InvoiceGlBreakoutItem } from 'src/services/useInvoiceGlBreakoutApi';
import { InvoiceLineItem } from 'src/services/useInvoiceLineItemsApi';
import { PoDetailsObject } from 'src/services/usePoDetailsApi';
import { PoHistory } from 'src/services/usePoHistoryApi';
import { SetState } from 'src/types/generic';
import { styles } from './PDFStyles';
pdfjs.GlobalWorkerOptions.workerSrc = '../../build/webpack/pdf.worker.bundle.js';

const formatDate = (date: string | Date) => (date ? format(new Date(date), 'MM/dd/yyyy') : '');

const displayDollarAmount = (amount: number | string) => `$${Number(amount).toFixed(2)}`;

Font.register({
  family: 'Roboto',
  src: font,
  fonts: [
    { src: font },
    {
      src: font,
      fontWeight: 700,
    },
  ],
});

interface LineItemColumn {
  title: string;
  key: keyof InvoiceLineItem;
  style: string;
  render?: (lineItem: InvoiceLineItem) => string;
}

const lineItemColumns: LineItemColumn[] = [
  {
    title: '#',
    key: 'poLineItemNumber',
    style: 'glAmount',
  },
  {
    title: 'Taxable',
    key: 'isTaxable',
    style: 'liAmount',
    render: (lineItem: InvoiceLineItem) => (lineItem.isTaxable ? 'Yes' : 'No'),
  },
  {
    title: 'PO Qty',
    key: 'poQty',
    style: 'liAmount',
  },
  {
    title: 'Cur. Doc Qty',
    key: 'quantityReceived',
    style: 'liAmount',
  },
  {
    title: 'Prev. Doc Qty',
    key: 'previousQty',
    style: 'liAmount',
  },
  {
    title: 'Variance Qty',
    key: 'varianceQty',
    style: 'liAmount',
  },
  {
    title: 'Item #',
    key: 'productItemNum',
    style: 'liAmount',
  },
  {
    title: 'Description',
    key: 'description',
    style: 'liContent',
  },
  {
    title: 'PO GL',
    key: 'poGlCd',
    style: 'liContent',
  },
  {
    title: 'UOM',
    key: 'uom',
    style: 'liAmount',
  },
  {
    title: 'Price',
    key: 'priceAmt',
    style: 'liAmount',
    render: (lineItem: InvoiceLineItem) => displayDollarAmount(lineItem.priceAmt),
  },
  {
    title: 'Item Subtotal',
    key: 'subTotal',
    style: 'liAmount',
    render: (lineItem: InvoiceLineItem) => displayDollarAmount(lineItem.subTotal),
  },
];

const historyColumns = [
  { title: 'Action', key: 'action', style: 'hcBig' },
  { title: 'Date', key: 'actionDateFormatted', style: 'hcSmall' },
  { title: 'Document', key: 'document', style: 'hcSmall' },
] as const;

const MpaGLCodesColumns = [
  { title: 'GL Code', key: 'glLabel', style: 'mpaGlGLCode' },
  { title: 'Accounting Period', key: 'acctPeriod', style: 'mapGLAcctPeriod' },
  { title: 'Notes', key: 'notes', style: 'mpaGlNotes' },
  { title: 'GL Subtotal', key: 'glSubTotal', style: 'glAmount' },
  { title: 'Taxable Amount', key: 'taxableAmount', style: 'glAmount' },
  { title: 'Discount', key: 'discountAmt', style: 'glAmount' },
  { title: 'Shipping', key: 'shipCostAmt', style: 'glAmount' },
  { title: 'Taxes', key: 'salesTaxAmt', style: 'glAmount' },
  { title: 'GL Total', key: 'glTotal', style: 'glAmount' },
] as const;
const glCodesColumns = [
  { title: 'GL Code', key: 'glLabel', style: 'glGLCode' },
  { title: 'Notes', key: 'notes', style: 'glNotes' },
  { title: 'GL Subtotal', key: 'glSubTotal', style: 'glAmount' },
  { title: 'Taxable Amount', key: 'taxableAmount', style: 'glAmount' },
  { title: 'Discount', key: 'discountAmt', style: 'glAmount' },
  { title: 'Shipping', key: 'shipCostAmt', style: 'glAmount' },
  { title: 'Taxes', key: 'salesTaxAmt', style: 'glAmount' },
  { title: 'GL Total', key: 'glTotal', style: 'glAmount' },
] as const;

const nonDollarGlCodesColumns = ['glLabel', 'notes'];

export interface PDFProps {
  isHistoryIncluded: boolean;
  isNotesIncluded: boolean;
  title: string;
  invoice: InvoiceDetailsResponse;
  invoiceId: string;
  poHistory: PoHistory;
  invoiceLineItems: InvoiceLineItem[];
  invoiceGlCodes: InvoiceGlBreakoutItem[];
  showAttachment: boolean;
  attachDocuments: string[];
  isPoHistoryLoading: boolean;
  setIsPrintLoading: SetState<boolean>;
  isInvoiceMPA: boolean;
  invoiceMPAData: (GLBreakoutLineItems | undefined)[];
  resolveAttachmentDocs: string[];
  poDetails: PoDetailsObject;
}

const PDF: FC<PDFProps> = ({
  isHistoryIncluded,
  isNotesIncluded,
  title,
  invoice,
  invoiceId,
  poHistory,
  invoiceLineItems,
  invoiceGlCodes,
  showAttachment,
  attachDocuments,
  setIsPrintLoading,
  isInvoiceMPA,
  invoiceMPAData,
  resolveAttachmentDocs,
  poDetails,
}) => {
  const buyerDetails = poDetails?.buyer;
  const supplierDetails = poDetails?.supplier;

  const invoiceInfoColumns = useMemo(
    () => [
      { title: 'PO #', value: invoice?.poNum },
      { title: 'Status', value: invoice?.status },
      {
        title: `${title} Date`,
        value: invoice?.invoiceDate ? formatDate(invoice?.invoiceDate) : '',
      },
      {
        title: 'Accounting Period',
        value: isInvoiceMPA
          ? 'Multiple Periods Assigned'
          : `${formatDate(invoice?.budgetPeriodStartDate)} - ${formatDate(
              invoice?.budgetPeriodEndDate
            )}`,
      },
      {
        title: 'Received Date',
        value: invoice?.receivedDate ? formatDate(invoice?.receivedDate) : '',
      },
    ],
    [
      invoice?.budgetPeriodEndDate,
      invoice?.budgetPeriodStartDate,
      invoice?.invoiceDate,
      invoice?.poNum,
      invoice?.receivedDate,
      invoice?.status,
      isInvoiceMPA,
      title,
    ]
  );
  const invoiceInfoColumns2 = useMemo(
    () => [
      { title: 'Discount', value: invoice?.discountAmt },
      { title: 'Shipping', value: invoice?.shipCostAmt },
      { title: 'Taxes', value: invoice?.salesTaxAmt },
      { title: 'Grand Total', value: invoice?.totalCostAmt },
    ],
    [invoice?.discountAmt, invoice?.salesTaxAmt, invoice?.shipCostAmt, invoice?.totalCostAmt]
  );

  return (
    <Document>
      <Page size="A4" style={styles.body}>
        <Text style={styles.title}>
          {title} # {invoice?.invoiceNumber || invoiceId}
        </Text>
        <View style={styles.pageFlow}>
          <View style={{ ...styles.POSection, ...styles.section }}>
            <View style={styles.POSectionColumn}>
              {invoiceInfoColumns.map((column) => (
                <View style={styles.poSectionItem} key={column.title}>
                  <Text style={styles.poSectionHeading}>{column.title}</Text>
                  <Text style={styles.poSectionValue}>{column.value}</Text>
                </View>
              ))}
            </View>
            <View style={styles.POSectionColumn}>
              {invoiceInfoColumns2.map((column) => (
                <View style={styles.poSectionItem} key={column.title}>
                  <Text style={styles.poSectionHeading}>{column.title}</Text>
                  <Text style={styles.poSectionValue}>{displayDollarAmount(column.value)}</Text>
                </View>
              ))}
            </View>
          </View>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Contact Information</Text>
            <View style={styles.divider} />

            <View style={styles.contactInfo}>
              <View style={styles.contactInfoSection}>
                <Text style={styles.subTitle}>Buyer Details:</Text>
                <Text style={styles.description}>{buyerDetails?.name}</Text>
                <Text style={styles.description}>{buyerDetails?.companyName}</Text>
                <Text style={styles.description}>{buyerDetails?.addressLine1}</Text>
                <Text style={styles.description}>{buyerDetails?.addressLine2}</Text>
                <Text style={styles.description}>{buyerDetails?.city}</Text>
                <Text style={styles.description}>{buyerDetails?.state}</Text>
                <Text style={styles.description}>{buyerDetails?.zipCode}</Text>
                <Text style={styles.description}>Phone: {buyerDetails?.phone}</Text>
                <Text style={styles.description}>Email: {buyerDetails?.email}</Text>
                <Text style={styles.description}>Fax: {buyerDetails?.fax}</Text>
              </View>
              <View style={styles.contactInfoSection}>
                <Text style={styles.subTitle}>Supplier Details:</Text>
                <Text style={styles.description}>{supplierDetails?.name}</Text>
                <Text style={styles.description}>{supplierDetails?.companyName}</Text>
                <Text style={styles.description}>{supplierDetails?.addressLine1}</Text>
                <Text style={styles.description}>{supplierDetails?.addressLine2}</Text>
                <Text style={styles.description}>{supplierDetails?.city}</Text>
                <Text style={styles.description}>{supplierDetails?.state}</Text>
                <Text style={styles.description}>{supplierDetails?.zipCode}</Text>
                <Text style={styles.description}>Phone: {supplierDetails?.phone}</Text>
                <Text style={styles.description}>Email: {supplierDetails?.email}</Text>
                <Text style={styles.description}>Fax: {supplierDetails?.fax}</Text>
              </View>
            </View>
          </View>

          {isNotesIncluded && (
            <View style={{ ...styles.section }}>
              <Text style={{ ...styles.sectionTitle }}>Notes</Text>
              <View style={styles.divider} />
              <Text style={styles.description}>{invoice?.notes || ''}</Text>
            </View>
          )}
          <View>
            <Text style={styles.sectionTitle}>GL codes</Text>
            <View style={styles.divider} />
          </View>

          <View style={styles.table}>
            <View style={styles.tableHeader}>
              {(isInvoiceMPA ? MpaGLCodesColumns : glCodesColumns).map((column, key) => (
                <View
                  key={key}
                  style={{
                    ...styles.tableCol,
                    ...(styles[column.style as keyof typeof styles] || {}),
                  }}
                >
                  <Text style={styles.tableHeaderCell}>{column.title}</Text>
                </View>
              ))}
            </View>
            {isInvoiceMPA && (
              <>
                {invoiceMPAData?.map((el: any) => (
                  <View style={styles.tableBody} key={Math.random()}>
                    <View style={{ width: '23%' }}>
                      <Text
                        style={{
                          fontSize: '8px',
                          textAlign: 'left',
                        }}
                      >
                        {el?.glLabel}
                      </Text>
                    </View>
                    <View style={{ width: '20%' }}>
                      <Text style={styles.tableCell}>{el?.acctPeriod}</Text>
                    </View>
                    <View style={{ width: '10%', marginRight: '2px' }}>
                      <View style={{ ...styles.tableCol, ...(styles.glAmount || {}) }}>
                        <Text style={styles.tableCell}>{el?.notes}</Text>
                      </View>
                    </View>
                    <View style={{ ...(styles.glAmount || {}) }}>
                      <View style={{ ...styles.tableCol, ...(styles.glAmount || {}) }}>
                        <Text style={styles.tableCell}>
                          {displayDollarAmount(el?.glSubTotal as number)}
                        </Text>
                      </View>
                    </View>
                    <View style={{ flex: 1, ...(styles.glAmount || {}) }}>
                      <View style={{ ...styles.tableCol, ...(styles.glAmount || {}) }}>
                        <Text style={styles.tableCell}>
                          {displayDollarAmount(el?.taxableAmount as number)}
                        </Text>
                      </View>
                    </View>
                    <View style={{ flex: 1, ...(styles.glAmount || {}) }}>
                      <View style={{ ...styles.tableCol, ...(styles.glAmount || {}) }}>
                        <Text style={styles.tableCell}>
                          {displayDollarAmount(el?.discountAmt as number)}
                        </Text>
                      </View>
                    </View>
                    <View style={{ flex: 1, ...(styles.glAmount || {}) }}>
                      <View style={{ ...styles.tableCol, ...(styles.glAmount || {}) }}>
                        <Text style={styles.tableCell}>
                          {displayDollarAmount(el?.shipCostAmt as number)}
                        </Text>
                      </View>
                    </View>
                    <View style={{ flex: 1, ...(styles.glAmount || {}) }}>
                      <View style={{ ...styles.tableCol, ...(styles.glAmount || {}) }}>
                        <Text style={styles.tableCell}>
                          {displayDollarAmount(el?.salesTaxAmt as number)}
                        </Text>
                      </View>
                    </View>
                    <View style={{ flex: 1, ...(styles.glAmount || {}) }}>
                      <View style={{ ...styles.tableCol, ...(styles.glAmount || {}) }}>
                        <Text style={styles.tableCell}>
                          {displayDollarAmount(el?.glTotal as number)}
                        </Text>
                      </View>
                    </View>
                  </View>
                ))}
              </>
            )}

            {!isInvoiceMPA && (
              <>
                {invoiceGlCodes?.map((glCode, rowIndex: number) => (
                  <View style={styles.tableBody} key={rowIndex}>
                    {glCodesColumns.map((column, index: number) => (
                      <View
                        key={index}
                        style={{ ...styles.tableCol, ...(styles[column.style] || {}) }}
                      >
                        <Text style={styles.tableCell}>
                          {nonDollarGlCodesColumns.includes(column.key)
                            ? glCode[column.key]
                            : displayDollarAmount(glCode[column.key] as number)}
                        </Text>
                      </View>
                    ))}
                  </View>
                ))}
              </>
            )}
          </View>

          {/* LineItems */}
          <View>
            <Text style={styles.sectionTitle}>Line Items</Text>
            <View style={styles.divider} />
          </View>

          <View style={styles.table}>
            <View style={styles.tableHeader}>
              {lineItemColumns.map((column, key) => (
                <View
                  key={key}
                  style={{
                    ...styles.tableCol,
                    ...(styles[column.style as keyof typeof styles] || {}),
                  }}
                >
                  <Text style={styles.tableHeaderCell}>{column.title}</Text>
                </View>
              ))}
            </View>

            {invoiceLineItems?.map((lineItem, rowIndex: number) => (
              <View key={rowIndex} style={styles.tableBody}>
                {lineItemColumns.map((column, index: number) => (
                  <View
                    key={index}
                    style={{
                      ...styles.tableCol,
                      ...(styles[column.style as keyof typeof styles] || {}),
                    }}
                  >
                    <Text key={index} style={styles.tableCell}>
                      {column.render ? column?.render(lineItem) : lineItem[column.key]?.toString()}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
          </View>

          {isHistoryIncluded && (
            <View>
              <View>
                <Text style={styles.sectionTitle}>{title} History</Text>
                <View style={styles.divider} />
              </View>

              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  {historyColumns.map((column, key) => (
                    <View key={key} style={{ ...styles.tableCol, ...(styles[column.style] || {}) }}>
                      <Text style={styles.tableHeaderCell} key={column.key}>
                        {column.title}
                      </Text>
                    </View>
                  ))}
                </View>
                {poHistory?.map((historyItem, rowIndex: number) => (
                  <View key={rowIndex} style={styles.tableBody}>
                    {historyColumns.map((column, index) => (
                      <View
                        key={index}
                        style={{ ...styles.tableCol, ...(styles[column.style] || {}) }}
                      >
                        <Text key={column.key} style={styles.tableCell}>
                          {historyItem[column.key]}
                        </Text>
                      </View>
                    ))}
                  </View>
                ))}
              </View>
            </View>
          )}
          {showAttachment && (
            <View>
              <View>
                <Text style={styles.sectionTitle}>Attached Documents</Text>
                <View style={styles.divider} />
                <View>
                  <>
                    {resolveAttachmentDocs.map((url: string, indx: number) =>
                      url.includes('data:') ? (
                        <Image key={indx} src={url} style={{ height: '700px' }} />
                      ) : (
                        <Image key={indx} src={url} />
                      )
                    )}
                  </>
                </View>
              </View>
            </View>
          )}
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

export default PDF;
